import { useAtomValue } from 'jotai';
import { pick } from 'lodash-es';
import { IFormatterDataModel } from '../../../data-models/formatter.data-model';
import {
  FundWaterfallResponse,
  PER_CLASS_PROCEEDS_KEYS,
  PER_CLASS_PROCEEDS_LABELS,
  PerClassProceedsKey,
} from '../../../data-models/fund-waterfall.data-model';
import { FundData, fundDataFormSchema } from '../../../schemas/FundData.schema';
import { FundMetricsV2, fundMetricsV2FormSchema } from '../../../schemas/FundMetricsV2.schema';
import { TierDataFormulas, TierFieldToLabel } from '../../../schemas/FundWaterfall.schema';
import { FMT } from '../../../util/formatter-service';
import { StringFormatterId } from '../../../util/formatters/StringFormatters';
import { schemaToFormFields } from '../../../util/schema-utils';
import { selectedDateStringFPState, selectedFundIdStateFP } from '../state/FPState';
import { waterfallDataAtom } from './FPWaterfallData3';

export type TierFormulaField = keyof TierDataFormulas;
export type FormulaField = {
  formatter?: string | IFormatterDataModel<unknown>;
  key: string;
  label?: string;
  value?: number | null;
};

export type FormulaInputField = FormulaField & {
  groupName?: string;
  key: keyof FundMetricsV2 | keyof FundData;
};

export type CalculatedField = FormulaField & {
  key: string; // e.g. "carry-0", "gp-1" - the number is the tier order
  formula: string | null;
  inputs: Set<string>;
  tierName?: string | null;
  tierOrder: number;
};
export function isCalculatedField(field: unknown): field is CalculatedField {
  return Object.prototype.hasOwnProperty.call(field, 'formula');
}

export function createFormulaField(overrides: Partial<FormulaField> = {}): FormulaField {
  return {
    key: '',
    label: '',
    formatter: '',
    value: null,
    ...overrides,
  };
}
export function createCalculatedField(overrides: Partial<CalculatedField> = {}): CalculatedField {
  return {
    ...createFormulaField(overrides),
    formula: null,
    inputs: new Set(),
    tierOrder: 0,
    tierName: null,
    ...overrides,
  };
}

export function useWaterfallFormulas() {
  const fundId = useAtomValue(selectedFundIdStateFP);
  const asOfDate = useAtomValue(selectedDateStringFPState);
  const { data } = useAtomValue(
    waterfallDataAtom({
      fundId: fundId ?? -1,
      asOfDate,
    })
  );

  return getWaterfallFormulas(data);
}

function getWaterfallFormulas(data: Partial<FundWaterfallResponse> = {}) {
  const { parameters: { basicParameters, customParameters } = {}, configuration, results } = data;

  const keyToFormulaField = getFormulaFieldMap();
  const basicParams = getParamFields(keyToFormulaField, basicParameters);
  const customParams = getParamFields(keyToFormulaField, customParameters);
  const allParams = new Set([
    ...basicParams.map((param) => param.key),
    ...customParams.map((param) => param.key),
    'totalProceeds',
    'total',
    'lpTotal',
    'slpTotal',
    'gpTotal',
  ]);

  const totalProceedsField: CalculatedField = getTotalProceedsField(allParams, {
    formula: configuration?.totalProceeds ?? null,
    value: results?.totalProceeds,
  });

  const perClassProceedsFields: CalculatedField[] = PER_CLASS_PROCEEDS_KEYS.map((key) =>
    getPerClassProceedsField(key, allParams, {
      formula: configuration?.[key] ?? null,
      value: results?.[key] as number | null | undefined,
    })
  );

  const tierFields = getTierFields(data, keyToFormulaField, allParams);

  return {
    basicParams,
    customParams,
    tierFields,
    totalProceedsField,
    perClassProceedsFields,
    allParams,
  };
}

export function getFormulaFieldMap(): Map<string, FormulaField> {
  const inputFieldMap = [
    ...schemaToFormFields(fundMetricsV2FormSchema()),
    ...schemaToFormFields(fundDataFormSchema()),
  ].reduce((map, field) => {
    const tierField = pick(field, ['key', 'label', 'formatter']);
    return map.set(field.key, tierField);
  }, new Map<string, FormulaField>());

  const calculatedFieldMap = getCalculatedFields().reduce((map, field) => {
    return map.set(field.key, field);
  }, new Map<string, FormulaField>());

  // Add manually-injected formula variables that aren't part of any entity schema
  inputFieldMap.set('hurdle', { key: 'hurdle', label: 'Hurdle', formatter: 'currency' });

  return new Map<string, FormulaField>([...inputFieldMap, ...calculatedFieldMap]);
}

function getCalculatedFields(): FormulaField[] {
  return Object.keys(TierFieldToLabel).map((key) => ({
    key,
    label: TierFieldToLabel[key as TierFormulaField] ?? key,
    formatter: 'currency',
  }));
}

function getParamFields(fieldsByKey: Map<string, FormulaField>, params?: Record<string, number | null>) {
  return Object.entries(params ?? {}).reduce((acc, [key, value]) => {
    const field = fieldsByKey.get(key) ?? {
      key,
      label: FMT.format(StringFormatterId.camelCaseToCapitalizedString, key),
      formatter: 'number',
    };
    acc.push({ ...field, value } as FormulaInputField);
    return acc;
  }, [] as FormulaInputField[]);
}

function getTotalProceedsField(
  params: Set<string>,
  overrides: { formula?: string | null; value?: number | null } = {}
) {
  return {
    key: 'totalProceeds',
    label: 'Total Proceeds',
    formatter: 'currency',
    formula: overrides?.formula ?? null,
    inputs: getFormulaInputs(overrides?.formula || null, params),
    tierOrder: 0,
    value: overrides?.value ?? null,
  };
}

function getPerClassProceedsField(
  key: PerClassProceedsKey,
  params: Set<string>,
  overrides: { formula?: string | null; value?: number | null } = {}
): CalculatedField {
  return {
    key,
    label: PER_CLASS_PROCEEDS_LABELS[key],
    formatter: 'currency',
    formula: overrides?.formula ?? null,
    inputs: getFormulaInputs(overrides?.formula || null, params),
    tierOrder: 0,
    value: overrides?.value ?? 0,
  };
}

export function getTierFields(
  data: Partial<FundWaterfallResponse> = {},
  keyToFormulaField: Map<string, FormulaField>,
  allParams: Set<string>
) {
  const { configuration, results } = data;
  return Object.entries(configuration?.tiers ?? {}).reduce((acc, [tierOrder, tierConfig]) => {
    Object.entries(tierConfig.formulas).forEach(([field, formula]) => {
      acc.push({
        ...(keyToFormulaField.get(field) ?? {}),
        key: getUniqueTierFieldKey(field as TierFormulaField, tierOrder as `${number}`),
        formula,
        inputs: getFormulaInputs(formula || null, allParams),
        tierOrder: parseInt(tierOrder),
        tierName: tierConfig.tierName,
        value: results?.tiers?.[parseInt(tierOrder)]?.[field as TierFormulaField],
      });
    }, [] as CalculatedField[]);

    return acc;
  }, [] as CalculatedField[]);
}

function getUniqueTierFieldKey(
  field: TierFormulaField,
  tierOrder: number | `${number}`
): `${TierFormulaField}-${number}` {
  return `${field}-${tierOrder}`;
}

export function uniqueTierFieldKeyToPath(key: string): TierFormulaField {
  return key.replace(/-(\d+)$/, '') as TierFormulaField;
}

function getFormulaInputs(formula: string | null, params: Set<string>) {
  if (!formula) {
    return new Set<string>();
  }
  return new Set(
    formula.split(/\s+/).reduce((acc, token) => {
      if (params.has(token)) {
        acc.push(token);
      }
      return acc;
    }, [] as string[])
  );
}
